import type { PublishBatchInput, PublishBatchRequestEntry, PublishInput } from '@aws-sdk/client-sns';
import { SNS } from '@aws-sdk/client-sns';
import type { SNSMessageAttributes } from '@weft/types';
import { awaitAllSafely, batch, batchByMaxPayloadSize } from '@weft/utils';
import type { SNSEvent } from 'aws-lambda';
import { MAX_BATCH_SIZE, MAX_MESSAGE_PAYLOAD_SIZE } from './sns-consts';
import type { SNSInputAdditionalData } from './sns-types';

const sns = new SNS();

export function getSnsMessage(event: SNSEvent) {
  const { Message } = event.Records[0].Sns;

  return Message;
}

export async function publishSnsMessage<Payload>(payloadObject: Payload, topicArn: string) {
  const publishInput = buildPublishInput(payloadObject, topicArn);

  return publishSns(publishInput, topicArn);
}

export function publishSnsMessageWithAdditionalInput<Payload>(payloadObject: Payload, input: SNSInputAdditionalData, topicArn: string) {
  const publishInput = buildPublishInput(payloadObject, topicArn, input);

  return publishSns(publishInput, topicArn);
}

export function publishSnsMessageWithAttributes<Payload>(payloadObject: Payload, attributes: SNSMessageAttributes, topicArn: string) {
  const publishInput = buildPublishInput(payloadObject, topicArn, { MessageAttributes: attributes });

  return publishSns(publishInput, topicArn);
}

export function publishBatchSnsMessages<Payload>(payloadObjects: Payload[], topicArn: string, getId: (payload: Payload) => string) {
  const publishBatchInputs = buildPublishBatchInputs(payloadObjects, topicArn, getId);

  const publishingMessages = publishBatchInputs.map(publishBatchInput => sns.publishBatch(publishBatchInput));
  console.info(`Publishing ${publishBatchInputs.length} SNS message batches to topic: ${topicArn}`);

  return awaitAllSafely(...publishingMessages);
}

async function publishSns(publishInput: PublishInput, topicArn: string) {
  const publishResult = await sns.publish(publishInput);

  console.info('Published message to SNS', { topicArn, messageId: publishResult.MessageId });

  return publishResult;
}

function buildPublishBatchInputs<Payload>(payloadObjects: Payload[], topicArn: string, getId: (payload: Payload) => string): PublishBatchInput[] {
  const entries = payloadObjects.map(payloadObject => {
    const id = getId(payloadObject);

    return buildPublishBatchRequestEntry(payloadObject, id);
  });

  const quantityLimitBatches = batch(entries, MAX_BATCH_SIZE);
  const sizeLimitedBatches = quantityLimitBatches.flatMap(entriesBatch => batchByMaxPayloadSize(entriesBatch, MAX_MESSAGE_PAYLOAD_SIZE, JSON.stringify));

  return sizeLimitedBatches.map<PublishBatchInput>(entriesBatch => ({
    PublishBatchRequestEntries: entriesBatch,
    TopicArn: topicArn,
  }));
}

function buildPublishBatchRequestEntry<Payload>(payloadObject: Payload, id: string): PublishBatchRequestEntry {
  const message = JSON.stringify(payloadObject);

  return {
    Message: message,
    Id: id,
  };
}

function buildPublishInput<Payload>(payloadObject: Payload, topicArn: string, additionInput?: SNSInputAdditionalData): PublishInput {
  const message = JSON.stringify(payloadObject);

  return {
    Message: message,
    TopicArn: topicArn,
    ...additionInput,
  };
}